import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { looksLikePlate, type PlateDetection, type PlateSighting } from '@iot-dashboard/shared';
import { useDeleteVehiclePlate, usePlateThumbnail, useSaveVehiclePlate } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { errorMessage } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { useToast } from '../../components/Toast';
import { formatDateTime } from '../../lib/format';
import { nameBesidePlate, plateLabel } from '../../lib/plates';
import { useObjectUrl } from '../../lib/useObjectUrl';

export function KnownBadge({ known }: { known?: boolean }) {
  if (known === undefined) return <span className="muted">—</span>;
  return known ? (
    <span className="badge badge--info">On a list</span>
  ) : (
    <span className="badge badge--warning">Not on a list</span>
  );
}

/**
 * A vehicle's plate for a table cell: the plate, with the name Protect knows
 * the vehicle by beneath it. A vehicle Protect sends only by name shows the
 * name instead, marked as having no plate yet, and administrators get a link
 * to add one.
 */
export function VehiclePlate({
  vehicle,
  onShow,
  onAddPlate,
}: {
  vehicle: Pick<PlateSighting, 'plate' | 'vehicleName' | 'plateSource'> & { rawPlate?: string };
  onShow: () => void;
  onAddPlate?: () => void;
}) {
  const nameOnly = vehicle.plateSource === 'name';
  const name = nameBesidePlate(vehicle);
  return (
    <span className="plate-cell__text">
      <button
        type="button"
        className={nameOnly ? 'plate plate--name' : 'plate'}
        title="Show every sighting of this vehicle"
        onClick={onShow}
      >
        {plateLabel(vehicle)}
      </button>
      {nameOnly ? (
        <span className="plate-cell__sub">
          Plate not saved
          {onAddPlate ? (
            <>
              {' · '}
              <button type="button" className="link-button" onClick={onAddPlate}>
                Add plate
              </button>
            </>
          ) : null}
        </span>
      ) : name ? (
        <span className="plate-cell__sub">{name}</span>
      ) : null}
    </span>
  );
}

/**
 * Saves the plate of a vehicle Protect sends by name. It applies to the name,
 * so every read of the vehicle shows it, earlier ones included.
 */
function SavePlateForm({ tenantId, read, onDone }: { tenantId: string; read: PlateDetection; onDone: () => void }) {
  const save = useSaveVehiclePlate(tenantId);
  const remove = useDeleteVehiclePlate(tenantId);
  const toast = useToast();
  const [plate, setPlate] = useState(read.plateSource === 'saved' ? read.rawPlate : '');
  const name = read.vehicleName ?? '';
  const valid = looksLikePlate(plate);
  const busy = save.isPending || remove.isPending;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || !read.vehicleKey) return;
    try {
      const saved = await save.mutateAsync({ vehicleKey: read.vehicleKey, name, plate });
      toast.push(`${saved.rawPlate} saved as the plate of ${name}.`, 'success');
      onDone();
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };
  const forget = async () => {
    if (!read.vehicleKey) return;
    try {
      await remove.mutateAsync(read.vehicleKey);
      toast.push(`${name} no longer has a saved plate.`, 'success');
      onDone();
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };

  return (
    <form className="plate-form" onSubmit={submit}>
      <div className="plate-form__row">
        <input
          className="input mono"
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          placeholder="e.g. EFK 042"
          aria-label={`Plate of ${name}`}
          autoFocus
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
        />
        <button type="submit" className="btn btn--primary btn--sm" disabled={!valid || busy}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDone} disabled={busy}>
          Cancel
        </button>
        {read.plateSource === 'saved' ? (
          <button type="button" className="btn btn--danger btn--sm" onClick={forget} disabled={busy}>
            Remove
          </button>
        ) : null}
      </div>
      <p className="form-row__hint">
        The console sends the name "{name}" for this vehicle, not its plate. The plate saved here shows on every read of{' '}
        {name}, earlier ones included, and a search for it finds them.
      </p>
    </form>
  );
}

interface Props {
  tenantId: string;
  base: string;
  /** Newest first, as the API returns them; Newer and Older walk this order. */
  reads: PlateDetection[];
  index: number;
  /** Administrators may save the plate of a vehicle Protect sends by name. */
  canConfigure: boolean;
  /** Opens with the plate form showing, for the read it opened on. */
  editPlate?: boolean;
  onNavigate: (read: PlateDetection) => void;
  onShowVehicle: (read: PlateDetection) => void;
  onClose: () => void;
}

/**
 * One plate read at full size, for review. The arrow keys step to the next
 * newer or older read, whatever order the table behind is sorted in.
 */
export function PlateReadDialog({ tenantId, base, reads, index, canConfigure, editPlate, onNavigate, onShowVehicle, onClose }: Props) {
  const read = reads[index]!;
  const newer = index > 0 ? reads[index - 1] : undefined;
  const older = index < reads.length - 1 ? reads[index + 1] : undefined;
  const hasPicture = Boolean(read.hasThumbnail);
  const picture = usePlateThumbnail(tenantId, read.detectionId, hasPicture);
  const src = useObjectUrl(picture.data);
  const plate = plateLabel(read);
  const nameOnly = read.plateSource === 'name';
  // By read, so stepping to another read closes the form.
  const [editing, setEditing] = useState<string | null>(editPlate ? read.detectionId : null);
  const canEditPlate = canConfigure && Boolean(read.plateSource && read.vehicleKey && read.vehicleName);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && /^(INPUT|SELECT|TEXTAREA)$/.test(e.target.tagName)) return;
      if (e.key === 'ArrowLeft' && newer) onNavigate(newer);
      if (e.key === 'ArrowRight' && older) onNavigate(older);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [newer, older, onNavigate]);

  let missing: string | null = null;
  if (!hasPicture) {
    missing =
      'No picture was kept with this read. To include one with future reads, turn on "Use thumbnails" in the Alarm Manager rule.';
  } else if (picture.isError) {
    missing = errorMessage(picture.error);
  } else if (!src) {
    missing = 'Loading the picture…';
  }

  return (
    <Modal
      title={nameOnly ? plate : `Plate ${plate}`}
      onClose={onClose}
      width={760}
      footer={
        <>
          <button type="button" className="btn" disabled={!newer} onClick={() => newer && onNavigate(newer)}>
            <ChevronLeft size={16} aria-hidden="true" /> Newer
          </button>
          <button type="button" className="btn" disabled={!older} onClick={() => older && onNavigate(older)}>
            Older <ChevronRight size={16} aria-hidden="true" />
          </button>
          <span className="grow" />
          <button type="button" className="btn btn--primary" onClick={() => onShowVehicle(read)}>
            Every sighting
          </button>
        </>
      }
    >
      <div className="plate-photo">
        {src && !missing ? (
          <img
            className="plate-photo__image"
            src={src}
            alt={`Vehicle ${nameOnly ? `known as ${plate}` : `with plate ${plate}`}${read.deviceName ? ` at ${read.deviceName}` : ''}, ${formatDateTime(read.ts)}`}
          />
        ) : (
          <div className="plate-photo__empty">
            <ImageOff size={28} aria-hidden="true" />
            <span>{missing}</span>
          </div>
        )}
      </div>
      <dl className="kv">
        <dt>Plate</dt>
        <dd>
          {editing === read.detectionId && canEditPlate ? (
            <SavePlateForm tenantId={tenantId} read={read} onDone={() => setEditing(null)} />
          ) : (
            <span className="plate-cell__text">
              <span className="kv-inline">
                {nameOnly ? (
                  <span className="muted">Not saved</span>
                ) : (
                  <span className="plate plate--static">{plate}</span>
                )}
                {canEditPlate ? (
                  <button type="button" className="link-button" onClick={() => setEditing(read.detectionId)}>
                    {nameOnly ? 'Add plate' : 'Change'}
                  </button>
                ) : null}
              </span>
              {read.plateSource === 'saved' ? (
                <span className="plate-cell__sub">Saved on the dashboard for this vehicle's name</span>
              ) : nameOnly ? (
                <span className="plate-cell__sub">The console sent the vehicle's name, not its plate</span>
              ) : null}
            </span>
          )}
        </dd>
        <dt>Seen</dt>
        <dd>
          {formatDateTime(read.ts)} · <TimeAgo iso={read.ts} />
        </dd>
        <dt>Camera</dt>
        <dd>
          {read.deviceId ? (
            <Link to={`${base}/cameras/${encodeURIComponent(read.deviceId)}`}>{read.deviceName ?? read.deviceId}</Link>
          ) : (
            <span className="muted">Unknown camera</span>
          )}
        </dd>
        <dt>Vehicle</dt>
        <dd>
          <span className="kv-inline">
            <KnownBadge known={read.known} />
            {read.vehicleName ? <span>{read.vehicleName}</span> : null}
          </span>
        </dd>
        <dt>Alarm</dt>
        <dd>{read.alarmName ?? '—'}</dd>
      </dl>
    </Modal>
  );
}
